"use client";

import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { PreliminaryAnalyticalTable } from "./preliminary-analytical-table";
import { PreliminaryAnalyticalSummary } from "./preliminary-analytical-summary";
import { FilteredAnalyticalTable } from "./filtered-analytical-table";
import {
  BarChart3,
  RefreshCw,
  FileText,
  TrendingUp,
  Calculator,
  AlertTriangle,
  Building2,
  DollarSign,
  Save,
  FileSignature,
} from "lucide-react";
import { Textarea } from "../ui/textarea";
import { getCompanyName } from "@/lib/textron-data-processor";
import { useArchivalMode } from "@/contexts/ArchivalModeContext";
import PlanningSignoffDialog, { PlanningSignoffTokens } from "./PlanningSignoffDialog";
import { useAuth } from "@/contexts/AuthContext";
import { loadMiscSignoffs, getSignoffEntriesForCard, type MiscSignoffEntry } from "@/utils/misc-signoff-utils";
import { useEngagement } from "@/contexts/EngagementContext";
import { apiService } from "@/services/api-service";

interface PARData {
  par: string;
  closing_balance: number;
  opening_balance: number;
  variance: number;
  variance_percentage: number;
  exceeds_materiality: boolean;
  qualitatively_material?: "Yes" | "No" | "";
  considered_significant: "Significant" | "Not Significant" | "";
  remarks?: string;
  note_line?: string;
  type?: string;
}

interface MaterialityThresholds {
  group_materiality?: number;
  performance_materiality?: number;
  clearly_trivial_threshold?: number;
  revised_materiality?: number;
}

const WORKSPACE_NAME = "Preliminary Analytical Procedures";

/** Returns the immediately preceding 31/03 date for opening balance. E.g. 31/03/25 → 31/03/24; 30/09/25 → 31/03/25. */
function getImmediatelyPreceding31March(closingDateStr: string): string {
  const parts = closingDateStr.trim().split("/");
  if (parts.length !== 3) return "";
  const m = parseInt(parts[1], 10);
  const y = parseInt(parts[2], 10);
  if (isNaN(m) || isNaN(y)) return "";
  const fullYear = 2000 + y;
  const openingYear = m > 3 ? fullYear : fullYear - 1;
  const yy = openingYear % 100;
  return `31/03/${String(yy).padStart(2, "0")}`;
}

export function PreliminaryAnalyticsSection() {
  const { isArchivalMode } = useArchivalMode();
  const { user } = useAuth();
  const { currentEngagement } = useEngagement();
  const [userEngagementRole, setUserEngagementRole] = useState<string | null>(null);
  const [signoffDialogOpen, setSignoffDialogOpen] = useState(false);
  const [cardSignoffs, setCardSignoffs] = useState<MiscSignoffEntry[]>([]);
  const [companyName, setCompanyName] = useState<string>("");

  const loadCardSignoffs = React.useCallback(async () => {
    try {
      const data = await loadMiscSignoffs();
      setCardSignoffs(getSignoffEntriesForCard(WORKSPACE_NAME, WORKSPACE_NAME, data.signoff));
    } catch {
      setCardSignoffs([]);
    }
  }, []);

  useEffect(() => {
    loadCardSignoffs();
  }, [loadCardSignoffs]);

  useEffect(() => {
    const loadUserEngagementRole = async () => {
      if (!currentEngagement || !user) {
        setUserEngagementRole(null);
        return;
      }
      try {
        const team = await apiService.getEngagementTeam(currentEngagement.id);
        const currentUserMember = team.find((member: any) => String(member.id) === String(user.id));
        setUserEngagementRole(currentUserMember?.role ?? null);
      } catch {
        setUserEngagementRole(null);
      }
    };
    loadUserEngagementRole();
  }, [currentEngagement, user]);

  // Load company name
  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");
      } catch (error) {
        console.warn('Failed to load company name, using fallback');
        setCompanyName("ABC Private Limited");
      }
    };
    loadCompanyName();
  }, []);
  const [analyticalData, setAnalyticalData] = useState<PARData[]>([]);
  const [materiality, setMateriality] = useState<MaterialityThresholds>({});
  const [activeTab, setActiveTab] = useState("procedures");
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [loading, setLoading] = useState(false);
  const [balanceSheetData, setBalanceSheetData] = useState<PARData[]>([]);
  const [profitLossData, setProfitLossData] = useState<PARData[]>([]);
  const [closingBalanceDate, setClosingBalanceDate] = useState<string>("");
  const [openingBalanceDate, setOpeningBalanceDate] = useState<string>("");
  const [isLoadingBalanceSheet, setIsLoadingBalanceSheet] = useState<boolean>(true);
  const [isLoadingProfitLoss, setIsLoadingProfitLoss] = useState<boolean>(true);
  const [areResponsesLoaded, setAreResponsesLoaded] = useState<boolean>(false);
  const [areProfitLossResponsesLoaded, setAreProfitLossResponsesLoaded] = useState<boolean>(false);
  const [documentationData, setDocumentationData] = useState<{
    keyFindings: string;
    areasRequiringInvestigation: string;
    managementExplanations: string;
  }>({
    keyFindings: "",
    areasRequiringInvestigation: "",
    managementExplanations: "",
  });
  const [isDocumentationLoading, setIsDocumentationLoading] = useState<boolean>(true);
  const [isSavingDocumentation, setIsSavingDocumentation] = useState<boolean>(false);

  // Load existing analytical data
  useEffect(() => {
    const loadAnalyticalData = async () => {
      try {
        const data = await window.planning.readSection(
          companyName,
          "preliminary-analytical",
        );
        if (data && data.analyticalData) {
          setAnalyticalData(data.analyticalData);
        }
      } catch (error) {
        console.warn("Failed to load preliminary analytical data:", error);
      }
    };

    loadAnalyticalData();
  }, [companyName]);

  // Load materiality data
  useEffect(() => {
    const loadMaterialityData = async () => {
      try {
        const materialityData = await window.planning.readSection(
          companyName,
          "materiality",
        );

        const groupMateriality = materialityData?.[
          "Group materiality at planning stage"
        ]?.find((item: any) => item.id === "M-3")?.answer;

        const performanceMaterialityPercent = materialityData?.[
          "Group materiality at planning stage"
        ]?.find((item: any) => item.id === "M-5")?.answer;

        const clearlyTrivialPercent = materialityData?.[
          "Group materiality at planning stage"
        ]?.find((item: any) => item.id === "M-7")?.answer;

        const performanceMateriality =
          groupMateriality && performanceMaterialityPercent
            ? (parseFloat(groupMateriality) *
                parseFloat(performanceMaterialityPercent)) /
              100
            : undefined;

        const clearlyTrivialThreshold =
          groupMateriality && clearlyTrivialPercent
            ? (parseFloat(groupMateriality) *
                parseFloat(clearlyTrivialPercent)) /
              100
            : undefined;

        // Check for revised materiality
        const revisionData = materialityData?.revisionData;
        const revisedMateriality = revisionData?.determinedMateriality;

        setMateriality({
          group_materiality: groupMateriality
            ? parseFloat(groupMateriality)
            : undefined,
          performance_materiality: performanceMateriality,
          clearly_trivial_threshold: clearlyTrivialThreshold,
          revised_materiality: revisedMateriality
            ? parseFloat(revisedMateriality.toString())
            : undefined,
        });
      } catch (error) {
        console.warn("Failed to load materiality data:", error);
      }
    };

    loadMaterialityData();
  }, [companyName]);

  // Load Balance Sheet Items data from SharePoint
  useEffect(() => {
    const loadBalanceSheetData = async () => {
      setIsLoadingBalanceSheet(true);
      try {
        if (!(window as any).sharePointAPI) {
          setIsLoadingBalanceSheet(false);
          return;
        }

        // Fetch mapping data
        const mappingResult = await (window as any).sharePointAPI.downloadTrialBalanceMapping?.({ includeAmounts: true });
        if (!mappingResult?.success || !mappingResult?.data) return;

        const mappingData = mappingResult.data;

        // Get all unique "par" values where type is "Asset" or "Liability" or "Equity"
        const parSet = new Set<string>();
        const parToLedgerIds: Record<string, Set<number>> = {};

        // Collect all dates from amount fields in mapping data
        const allDates: string[] = [];

        for (const [bookName, entries] of Object.entries(mappingData)) {
          if (Array.isArray(entries)) {
            entries.forEach((entry: any) => {
              const entryType = entry.type || "";
              const entryPar = entry.par || "";
              
              // Collect dates from amount field
              if (entry.amount && typeof entry.amount === 'object') {
                Object.keys(entry.amount).forEach(date => {
                  if (!allDates.includes(date)) {
                    allDates.push(date);
                  }
                });
              }
              
              if ((entryType === "Asset" || entryType === "Liability" || entryType === "Equity") && entryPar) {
                parSet.add(entryPar);
                
                if (!parToLedgerIds[entryPar]) {
                  parToLedgerIds[entryPar] = new Set<number>();
                }
                
                const ledgerId = entry.ledger_id || entry.ledgerId;
                if (ledgerId) {
                  parToLedgerIds[entryPar].add(ledgerId);
                }
              }
            });
          }
        }

        // Parse dates and find the latest
        const parseDate = (dateStr: string): Date => {
          const [day, month, year] = dateStr.split('/');
          return new Date(2000 + parseInt(year), parseInt(month) - 1, parseInt(day));
        };

        const sortedDates = allDates.sort((a, b) => {
          const dateA = parseDate(a);
          const dateB = parseDate(b);
          return dateB.getTime() - dateA.getTime(); // Descending order
        });

        const latestDate = sortedDates[0];
        if (!latestDate) return;

        // Opening balance = immediately preceding 31/03 (e.g. closing 31/03/25 → 31/03/24; closing 30/09/25 → 31/03/25)
        const openingDate = getImmediatelyPreceding31March(latestDate);

        setClosingBalanceDate(latestDate);
        setOpeningBalanceDate(openingDate);

        // Calculate balances for each PAR
        const balanceSheetItems: PARData[] = Array.from(parSet).map((par) => {
          const ledgerIds = parToLedgerIds[par] || new Set<number>();
          
          // Calculate closing balance (latest date) from amount field in mapping data
          let closingBalance = 0;
          for (const [bookName, entries] of Object.entries(mappingData)) {
            if (Array.isArray(entries)) {
              entries.forEach((entry: any) => {
                const entryPar = entry.par || "";
                const entryLedgerId = entry.ledger_id || entry.ledgerId;
                
                if (entryPar === par && ledgerIds.has(entryLedgerId) && entry.amount) {
                  const amountData = entry.amount;
                  if (typeof amountData === 'object' && amountData[latestDate] !== undefined) {
                    closingBalance += parseFloat(amountData[latestDate]) || 0;
                  }
                }
              });
            }
          }

          // Calculate opening balance (previous FY date) from amount field in mapping data
          let openingBalance = 0;
          if (openingDate) {
            for (const [bookName, entries] of Object.entries(mappingData)) {
              if (Array.isArray(entries)) {
                entries.forEach((entry: any) => {
                  const entryPar = entry.par || "";
                  const entryLedgerId = entry.ledger_id || entry.ledgerId;
                  
                  if (entryPar === par && ledgerIds.has(entryLedgerId) && entry.amount) {
                    const amountData = entry.amount;
                    if (typeof amountData === 'object' && amountData[openingDate] !== undefined) {
                      openingBalance += parseFloat(amountData[openingDate]) || 0;
                    }
                  }
                });
              }
            }
          }

          const variance = closingBalance - openingBalance;
          const variance_percentage = openingBalance !== 0
            ? (variance / openingBalance) * 100
            : openingBalance === 0 && closingBalance !== 0
              ? 100
              : 0;

          // Get type for this PAR (find first entry with this par)
          let parType = "";
          for (const [bookName, entries] of Object.entries(mappingData)) {
            if (Array.isArray(entries)) {
              const entry = entries.find((e: any) => (e.par || "") === par);
              if (entry) {
                parType = entry.type || "";
                break;
              }
            }
          }

          return {
            par,
            closing_balance: closingBalance,
            opening_balance: openingBalance,
            variance,
            variance_percentage,
            exceeds_materiality: false, // Will be calculated based on materiality in FilteredAnalyticalTable
            considered_significant: "" as const,
            qualitatively_material: "" as const,
            remarks: "",
            type: parType,
          };
        });

        // Load saved responses from SharePoint
        let savedResponses: any[] = [];
        setAreResponsesLoaded(false);
        try {
          if ((window as any).planningAPI?.loadResponses) {
            const sharePointResponses = await (window as any).planningAPI.loadResponses({
              companyName,
              sectionKey: "preliminary-analytical",
            });

            console.log("SharePoint responses received:", sharePointResponses);

            if (sharePointResponses?.success && sharePointResponses?.data) {
              const responses = sharePointResponses.data.responses || sharePointResponses.data;
              console.log("Responses object:", responses);
              const savedData = responses["PreliminaryAnalyticalProcedures_BalanceSheetItems"];
              console.log("PreliminaryAnalyticalProcedures_BalanceSheetItems:", savedData);
              
              if (Array.isArray(savedData)) {
                savedResponses = savedData;
                console.log("Loaded saved responses from SharePoint:", savedResponses.length, "items");
              } else {
                console.log("No saved data found or data is not an array");
              }
            } else {
              console.log("SharePoint responses not successful or no data");
            }
          } else {
            console.log("planningAPI.loadResponses not available");
          }
        } catch (error) {
          console.error("Failed to load saved responses from SharePoint:", error);
        } finally {
          // Always mark as loaded after attempting to load (whether successful or not)
          setAreResponsesLoaded(true);
          console.log("areResponsesLoaded set to true");
        }

        // Merge saved responses with calculated data
        const mergedData = balanceSheetItems.map((item) => {
          // Find matching saved response by par/abcotd
          const savedItem = savedResponses.find(
            (saved: any) => (saved.par === item.par || saved.abcotd === item.par)
          );

          if (savedItem) {
            // Convert exceeds_materiality to boolean if it comes as string from SharePoint
            let exceedsMaterialityValue: boolean;
            const exceedsValue = savedItem.exceeds_materiality;
            if (typeof exceedsValue === "boolean") {
              exceedsMaterialityValue = exceedsValue;
            } else if (typeof exceedsValue === "string") {
              const strValue = String(exceedsValue).toLowerCase();
              exceedsMaterialityValue = strValue === "yes" || strValue === "true";
            } else if (exceedsValue !== undefined && exceedsValue !== null) {
              exceedsMaterialityValue = Boolean(exceedsValue);
            } else {
              // Fallback to calculated value if not in SharePoint
              exceedsMaterialityValue = item.exceeds_materiality;
            }

            return {
              ...item,
              exceeds_materiality: exceedsMaterialityValue,
              qualitatively_material: savedItem.qualitatively_material || "",
              considered_significant: savedItem.considered_significant || "",
              remarks: savedItem.remarks || "",
            };
          }
          return item;
        });

        // mergedData already contains SharePoint responses, now merge with any local data
        // (SharePoint data takes priority)
        const existingData = await window.planning.readSection(
          companyName,
          "preliminary-analytical",
        );
        const existingBalanceSheetData = existingData?.analyticalData?.filter(
          (item: PARData) => item.type === "Asset" || item.type === "Liability"
        ) || [];

        // Final merge: SharePoint data (already in mergedData) takes priority over local data
        const finalMergedData = mergedData.map((item) => {
          const existingItem = existingBalanceSheetData.find(
            (e: PARData) => e.par === item.par
          );
          // Only use local data if SharePoint doesn't have it
          if (existingItem && !item.qualitatively_material && !item.considered_significant && !item.remarks) {
            return {
              ...item,
              qualitatively_material: existingItem.qualitatively_material || "",
              considered_significant: existingItem.considered_significant || "",
              remarks: existingItem.remarks || "",
            };
          }
          return item;
        });

        setBalanceSheetData(finalMergedData);
        
        // Update analyticalData to include balance sheet items
        setAnalyticalData((prevData) => {
          const otherItems = prevData.filter(
            (item) => item.type !== "Asset" && item.type !== "Liability" && item.type !== "Equity"
          );
          return [...finalMergedData, ...otherItems];
        });
        
        // Save the updated data
        if (finalMergedData.length > 0) {
          const otherItems = analyticalData.filter(
            (item) => item.type !== "Asset" && item.type !== "Liability"
          );
          saveAnalyticalData([...finalMergedData, ...otherItems]);
        }
      } catch (error) {
        console.error("Failed to load Balance Sheet data from SharePoint:", error);
      } finally {
        setIsLoadingBalanceSheet(false);
      }
    };

    if (companyName) {
      loadBalanceSheetData();
    }
  }, [companyName]);

  // Load Profit & Loss Items data from SharePoint
  useEffect(() => {
    const loadProfitLossData = async () => {
      setIsLoadingProfitLoss(true);
      try {
        if (!(window as any).sharePointAPI) {
          setIsLoadingProfitLoss(false);
          return;
        }

        // Fetch mapping data
        const mappingResult = await (window as any).sharePointAPI.downloadTrialBalanceMapping?.({ includeAmounts: true });
        if (!mappingResult?.success || !mappingResult?.data) {
          setIsLoadingProfitLoss(false);
          return;
        }

        const mappingData = mappingResult.data;

        // Get all unique "par" values where type is "Income" or "Expense"
        const parSet = new Set<string>();
        const parToLedgerIds: Record<string, Set<number>> = {};

        // Collect all dates from amount fields in mapping data
        const allDates: string[] = [];

        for (const [bookName, entries] of Object.entries(mappingData)) {
          if (Array.isArray(entries)) {
            entries.forEach((entry: any) => {
              const entryType = entry.type || "";
              const entryPar = entry.par || "";
              
              // Collect dates from amount field
              if (entry.amount && typeof entry.amount === 'object') {
                Object.keys(entry.amount).forEach(date => {
                  if (!allDates.includes(date)) {
                    allDates.push(date);
                  }
                });
              }
              
              if ((entryType === "Income" || entryType === "Expense") && entryPar) {
                parSet.add(entryPar);
                
                if (!parToLedgerIds[entryPar]) {
                  parToLedgerIds[entryPar] = new Set<number>();
                }
                
                const ledgerId = entry.ledger_id || entry.ledgerId;
                if (ledgerId) {
                  parToLedgerIds[entryPar].add(ledgerId);
                }
              }
            });
          }
        }

        // Parse dates and find the latest
        const parseDate = (dateStr: string): Date => {
          const [day, month, year] = dateStr.split('/');
          return new Date(2000 + parseInt(year), parseInt(month) - 1, parseInt(day));
        };

        const sortedDates = allDates.sort((a, b) => {
          const dateA = parseDate(a);
          const dateB = parseDate(b);
          return dateB.getTime() - dateA.getTime();
        });

        const latestDate = sortedDates[0];
        if (!latestDate) {
          setIsLoadingProfitLoss(false);
          return;
        }

        // Opening balance = immediately preceding 31/03 (e.g. closing 31/03/25 → 31/03/24; closing 30/09/25 → 31/03/25)
        const openingDate = getImmediatelyPreceding31March(latestDate);

        // Calculate balances for each PAR
        const profitLossItems: PARData[] = Array.from(parSet).map((par) => {
          const ledgerIds = parToLedgerIds[par] || new Set<number>();
          
          // Calculate closing balance (latest date) from amount field in mapping data
          let closingBalance = 0;
          for (const [bookName, entries] of Object.entries(mappingData)) {
            if (Array.isArray(entries)) {
              entries.forEach((entry: any) => {
                const entryPar = entry.par || "";
                const entryLedgerId = entry.ledger_id || entry.ledgerId;
                
                if (entryPar === par && ledgerIds.has(entryLedgerId) && entry.amount) {
                  const amountData = entry.amount;
                  if (typeof amountData === 'object' && amountData[latestDate] !== undefined) {
                    closingBalance += parseFloat(amountData[latestDate]) || 0;
                  }
                }
              });
            }
          }

          // Calculate opening balance (previous FY date) from amount field in mapping data
          let openingBalance = 0;
          if (openingDate) {
            for (const [bookName, entries] of Object.entries(mappingData)) {
              if (Array.isArray(entries)) {
                entries.forEach((entry: any) => {
                  const entryPar = entry.par || "";
                  const entryLedgerId = entry.ledger_id || entry.ledgerId;
                  
                  if (entryPar === par && ledgerIds.has(entryLedgerId) && entry.amount) {
                    const amountData = entry.amount;
                    if (typeof amountData === 'object' && amountData[openingDate] !== undefined) {
                      openingBalance += parseFloat(amountData[openingDate]) || 0;
                    }
                  }
                });
              }
            }
          }

          const variance = closingBalance - openingBalance;
          const variance_percentage = openingBalance !== 0
            ? (variance / openingBalance) * 100
            : openingBalance === 0 && closingBalance !== 0
              ? 100
              : 0;

          // Get type for this PAR
          let parType = "";
          for (const [bookName, entries] of Object.entries(mappingData)) {
            if (Array.isArray(entries)) {
              const entry = entries.find((e: any) => (e.par || "") === par);
              if (entry) {
                parType = entry.type || "";
                break;
              }
            }
          }

          return {
            par,
            closing_balance: closingBalance,
            opening_balance: openingBalance,
            variance,
            variance_percentage,
            exceeds_materiality: false,
            considered_significant: "" as const,
            qualitatively_material: "" as const,
            remarks: "",
            type: parType,
          };
        });

        // Load saved responses from SharePoint for Profit & Loss
        let savedResponses: any[] = [];
        setAreProfitLossResponsesLoaded(false);
        try {
          if ((window as any).planningAPI?.loadResponses) {
            const sharePointResponses = await (window as any).planningAPI.loadResponses({
              companyName,
              sectionKey: "preliminary-analytical",
            });

            if (sharePointResponses?.success && sharePointResponses?.data) {
              const responses = sharePointResponses.data.responses || sharePointResponses.data;
              const savedData = responses["PreliminaryAnalyticalProcedures_ProfitLossItems"];
              
              if (Array.isArray(savedData)) {
                savedResponses = savedData;
                console.log("Loaded saved Profit & Loss responses from SharePoint:", savedResponses.length, "items");
              }
            }
          }
        } catch (error) {
          console.error("Failed to load saved Profit & Loss responses from SharePoint:", error);
        } finally {
          setAreProfitLossResponsesLoaded(true);
        }

        // Merge saved responses with calculated data
        const mergedData = profitLossItems.map((item) => {
          const savedItem = savedResponses.find(
            (saved: any) => (saved.par === item.par || saved.abcotd === item.par)
          );

          if (savedItem) {
            // Convert exceeds_materiality to boolean if it comes as string from SharePoint
            let exceedsMaterialityValue: boolean;
            const exceedsValue = savedItem.exceeds_materiality;
            if (typeof exceedsValue === "boolean") {
              exceedsMaterialityValue = exceedsValue;
            } else if (typeof exceedsValue === "string") {
              const strValue = String(exceedsValue).toLowerCase();
              exceedsMaterialityValue = strValue === "yes" || strValue === "true";
            } else if (exceedsValue !== undefined && exceedsValue !== null) {
              exceedsMaterialityValue = Boolean(exceedsValue);
            } else {
              // Fallback to calculated value if not in SharePoint
              exceedsMaterialityValue = item.exceeds_materiality;
            }

            return {
              ...item,
              exceeds_materiality: exceedsMaterialityValue,
              qualitatively_material: savedItem.qualitatively_material || "",
              considered_significant: savedItem.considered_significant || "",
              remarks: savedItem.remarks || "",
            };
          }
          return item;
        });

        setProfitLossData(mergedData);
        
        // Update analyticalData to include profit & loss items
        setAnalyticalData((prevData) => {
          const otherItems = prevData.filter(
            (item) => item.type !== "Income" && item.type !== "Expense"
          );
          return [...otherItems, ...mergedData];
        });
      } catch (error) {
        console.error("Failed to load Profit & Loss data from SharePoint:", error);
      } finally {
        setIsLoadingProfitLoss(false);
      }
    };

    if (companyName) {
      loadProfitLossData();
    }
  }, [companyName]);

  // Load Documentation data from SharePoint
  useEffect(() => {
    const loadDocumentationData = async () => {
      setIsDocumentationLoading(true);
      try {
        if (!(window as any).planningAPI?.loadResponses) {
          setIsDocumentationLoading(false);
          return;
        }

        const sharePointResponses = await (window as any).planningAPI.loadResponses({
          companyName,
          sectionKey: "preliminary-analytical",
        });

        if (sharePointResponses?.success && sharePointResponses?.data) {
          const responses = sharePointResponses.data.responses || sharePointResponses.data;
          const savedData = responses["PreliminaryAnalyticalProcedures_Documentation"];
          
          if (savedData && typeof savedData === "object") {
            setDocumentationData({
              keyFindings: savedData.keyFindings || "",
              areasRequiringInvestigation: savedData.areasRequiringInvestigation || "",
              managementExplanations: savedData.managementExplanations || "",
            });
            console.log("Loaded saved documentation from SharePoint");
          }
        }
      } catch (error) {
        console.error("Failed to load documentation from SharePoint:", error);
      } finally {
        setIsDocumentationLoading(false);
      }
    };

    if (companyName) {
      loadDocumentationData();
    }
  }, [companyName]);

  // Save Documentation data to SharePoint
  const handleSaveDocumentation = async () => {
    setIsSavingDocumentation(true);
    try {
      const currentCompanyName = companyName || await getCompanyName("combined") || "ABC Private Limited";
      
      const saveData: Record<string, any> = {
        PreliminaryAnalyticalProcedures_Documentation: {
          keyFindings: documentationData.keyFindings,
          areasRequiringInvestigation: documentationData.areasRequiringInvestigation,
          managementExplanations: documentationData.managementExplanations,
        },
      };

      console.log("Saving Documentation:", {
        companyName: currentCompanyName,
        sectionKey: "preliminary-analytical",
        responses: saveData,
      });

      if ((window as any).planningAPI?.saveAllResponses) {
        const result = await (window as any).planningAPI.saveAllResponses({
          companyName: currentCompanyName,
          sectionKey: "preliminary-analytical",
          responses: saveData,
        });

        if (result.success) {
          alert("Documentation saved successfully!");
          setLastSaved(new Date());
        } else {
          alert(`Failed to save documentation: ${result.error}`);
        }
      } else {
        console.error("planningAPI.saveAllResponses not available");
        alert("Save functionality not available.");
      }
    } catch (error: any) {
      console.error("Error saving documentation:", error);
      alert(`Error saving documentation: ${error.message || "Unknown error"}`);
    } finally {
      setIsSavingDocumentation(false);
    }
  };

  // Save analytical data
  const saveAnalyticalData = async (data: PARData[]) => {
    try {
      await window.planning.saveSection(companyName, "preliminary-analytical", {
        analyticalData: data,
      });
      setLastSaved(new Date());
    } catch (error) {
      console.error("Failed to save preliminary analytical data:", error);
    }
  };

  const handleAnalyticalDataChange = (data: PARData[]) => {
    setAnalyticalData(data);
    saveAnalyticalData(data);
  };

  const calculateStats = () => {
    const totalItems = analyticalData.length;
    const significantItems = analyticalData.filter(
      (item) => item.considered_significant === "Significant",
    ).length;
    const materialItems = analyticalData.filter(
      (item) => item.exceeds_materiality,
    ).length;
    const totalVariance = analyticalData.reduce(
      (sum, item) => sum + Math.abs(item.variance),
      0,
    );

    // Balance Sheet stats
    const balanceSheetItems = analyticalData.filter((item) =>
      ["Asset", "Liability"].includes(item.type || ""),
    );
    const bsSignificantItems = balanceSheetItems.filter(
      (item) => item.considered_significant === "Significant",
    ).length;

    // P&L stats
    const profitLossItems = analyticalData.filter((item) =>
      ["Income", "Expense"].includes(item.type || ""),
    );
    const plSignificantItems = profitLossItems.filter(
      (item) => item.considered_significant === "Significant",
    ).length;

    return {
      totalItems,
      significantItems,
      materialItems,
      totalVariance,
      balanceSheetItems: balanceSheetItems.length,
      bsSignificantItems,
      profitLossItems: profitLossItems.length,
      plSignificantItems,
    };
  };

  const stats = calculateStats();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">
            Preliminary Analytical Procedures
          </h2>
          <p className="text-white/70">
            Analytical procedures and variance analysis for {companyName}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <PlanningSignoffTokens signoffs={cardSignoffs} className="mt-0" />
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSignoffDialogOpen(true)}
            className="border-white/20 text-white hover:bg-white/10"
          >
            <FileSignature className="h-4 w-4 mr-1.5" />
            Sign off
          </Button>
          {lastSaved && (
            <Badge className="bg-emerald-500/15 text-emerald-300">
              Saved {lastSaved.toLocaleTimeString()}
            </Badge>
          )}
        </div>
      </div>

      <PlanningSignoffDialog
        isOpen={signoffDialogOpen}
        onClose={() => setSignoffDialogOpen(false)}
        workspaceName={WORKSPACE_NAME}
        cardNames={[WORKSPACE_NAME]}
        user={user ? { user_name: (user as any).user_name ?? (user as any).name, type: (user as any).type } : null}
        userEngagementRole={userEngagementRole}
        onSignoffDone={loadCardSignoffs}
      />

      {/* Overview Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-6">
        <Card className="border-white/10 bg-black/20">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm text-white/80">
              <BarChart3 className="h-4 w-4" />
              Total Items
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-white">
              {stats.totalItems}
            </div>
          </CardContent>
        </Card>

        <Card className="border-white/10 bg-black/20">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm text-white/80">
              <AlertTriangle className="h-4 w-4" />
              Significant Items
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-300">
              {stats.significantItems}
            </div>
          </CardContent>
        </Card>

        <Card className="border-white/10 bg-black/20">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm text-white/80">
              <Calculator className="h-4 w-4" />
              Material Items
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-300">
              {stats.materialItems}
            </div>
          </CardContent>
        </Card>

        <Card className="border-white/10 bg-black/20">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm text-white/80">
              <Building2 className="h-4 w-4" />
              Balance Sheet
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-lg font-bold text-blue-300">
              {stats.balanceSheetItems}
            </div>
            <div className="text-xs text-white/60">
              {stats.bsSignificantItems} significant
            </div>
          </CardContent>
        </Card>

        <Card className="border-white/10 bg-black/20">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm text-white/80">
              <DollarSign className="h-4 w-4" />
              Profit & Loss
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-lg font-bold text-green-300">
              {stats.profitLossItems}
            </div>
            <div className="text-xs text-white/60">
              {stats.plSignificantItems} significant
            </div>
          </CardContent>
        </Card>

        <Card className="border-white/10 bg-black/20">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm text-white/80">
              <TrendingUp className="h-4 w-4" />
              Total Variance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-lg font-bold text-purple-300">
              {new Intl.NumberFormat("en-IN", {
                style: "currency",
                currency: "INR",
                notation: "compact",
              }).format(stats.totalVariance)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Card className="border-white/10 bg-black/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <BarChart3 className="h-5 w-5" />
            Preliminary Analytical Procedures
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="w-full"
          >
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="procedures">
                <BarChart3 className="mr-2 h-4 w-4" />
                Procedures
              </TabsTrigger>
              <TabsTrigger value="summary">
                <FileText className="mr-2 h-4 w-4" />
                Summary
              </TabsTrigger>
              <TabsTrigger value="documentation">
                <FileText className="mr-2 h-4 w-4" />
                Documentation
              </TabsTrigger>
            </TabsList>

            <TabsContent value="procedures" className="mt-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-white">
                    Analytical Procedures Table
                  </h3>
                  <Button
                    onClick={() => {
                      // This will trigger the refresh in the table component
                      setLoading(true);
                      setTimeout(() => setLoading(false), 1000);
                    }}
                    disabled={loading}
                    size="sm"
                    variant="outline"
                    className="border-white/10 text-white/80 hover:bg-white/10"
                  >
                    <RefreshCw
                      className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`}
                    />
                    {loading ? "Loading..." : "Refresh All Data"}
                  </Button>
                </div>

                <PreliminaryAnalyticalTable
                  value={analyticalData}
                  onChange={handleAnalyticalDataChange}
                  readonly={false}
                />

                {/* Balance Sheet Table */}
                <div className="mt-8">
                  {isLoadingBalanceSheet ? (
                    <Card className="border-white/10 bg-black/20">
                      <CardContent className="flex items-center justify-center py-12">
                        <div className="flex flex-col items-center gap-4">
                          <RefreshCw className="h-8 w-8 animate-spin text-white/60" />
                          <p className="text-white/70">Loading Balance Sheet Items...</p>
                        </div>
                      </CardContent>
                    </Card>
                  ) : (
                    <FilteredAnalyticalTable
                      title="Balance Sheet Items"
                      data={balanceSheetData}
                      materiality={materiality}
                      onChange={handleAnalyticalDataChange}
                      readonly={isArchivalMode}
                      filterTypes={["Asset", "Liability", "Equity"]}
                      excludeTypes={["Income", "Expense"]}
                      icon={<Building2 className="h-5 w-5" />}
                      useYesNoForSignificance={true}
                      closingBalanceDate={closingBalanceDate}
                      openingBalanceDate={openingBalanceDate}
                      showAbcotd={true}
                      showSaveButton={true}
                      areResponsesLoaded={areResponsesLoaded}
                    />
                  )}
                </div>

                {/* Profit & Loss Table */}
                <div className="mt-8">
                  {isLoadingProfitLoss ? (
                    <Card className="border-white/10 bg-black/20">
                      <CardContent className="flex items-center justify-center py-12">
                        <div className="flex flex-col items-center gap-4">
                          <RefreshCw className="h-8 w-8 animate-spin text-white/60" />
                          <p className="text-white/70">Loading Profit & Loss Items...</p>
                        </div>
                      </CardContent>
                    </Card>
                  ) : (
                    <FilteredAnalyticalTable
                      title="Profit & Loss Items"
                      data={profitLossData}
                      materiality={materiality}
                      onChange={handleAnalyticalDataChange}
                      readonly={isArchivalMode}
                      filterTypes={["Income", "Expense"]}
                      excludeTypes={["Asset", "Liability", "Equity"]}
                      icon={<DollarSign className="h-5 w-5" />}
                      useYesNoForSignificance={true}
                      closingBalanceDate={closingBalanceDate}
                      openingBalanceDate={openingBalanceDate}
                      showAbcotd={true}
                      showSaveButton={true}
                      areResponsesLoaded={areProfitLossResponsesLoaded}
                    />
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="summary" className="mt-6">
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-white">
                  Analytical Procedures Summary
                </h3>
                <PreliminaryAnalyticalSummary
                  data={analyticalData}
                  materiality={materiality}
                />
              </div>
            </TabsContent>

            <TabsContent value="documentation" className="mt-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-white">
                    Documentation & Findings
                  </h3>
                  <Button
                    onClick={handleSaveDocumentation}
                    disabled={isSavingDocumentation || isDocumentationLoading || isArchivalMode}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {isSavingDocumentation ? (
                      <>
                        <Save className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        Save
                      </>
                    )}
                  </Button>
                </div>
                {isDocumentationLoading ? (
                  <Card className="border-white/10 bg-black/20">
                    <CardContent className="flex items-center justify-center py-12">
                      <div className="flex flex-col items-center gap-4">
                        <RefreshCw className="h-8 w-8 animate-spin text-white/60" />
                        <p className="text-white/70">Loading Documentation...</p>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="rounded-lg border border-white/10 bg-black/10 p-6">
                    <div className="space-y-4">
                      <div>
                        <h4 className="mb-2 font-medium text-white">
                          Key Findings
                        </h4>
                        <Textarea
                          value={documentationData.keyFindings}
                          onChange={(e) =>
                            setDocumentationData({
                              ...documentationData,
                              keyFindings: e.target.value,
                            })
                          }
                          disabled={isArchivalMode}
                          className="min-h-32 w-full border-white/10 bg-black/40 text-white placeholder:text-white/50"
                          placeholder="Document key findings from preliminary analytical procedures..."
                        />
                      </div>
                      <div>
                        <h4 className="mb-2 font-medium text-white">
                          Areas Requiring Further Investigation
                        </h4>
                        <Textarea
                          value={documentationData.areasRequiringInvestigation}
                          onChange={(e) =>
                            setDocumentationData({
                              ...documentationData,
                              areasRequiringInvestigation: e.target.value,
                            })
                          }
                          disabled={isArchivalMode}
                          className="min-h-32 w-full border-white/10 bg-black/40 text-white placeholder:text-white/50"
                          placeholder="Identify areas that require additional audit procedures..."
                        />
                      </div>
                      <div>
                        <h4 className="mb-2 font-medium text-white">
                          Management Explanations
                        </h4>
                        <Textarea
                          value={documentationData.managementExplanations}
                          onChange={(e) =>
                            setDocumentationData({
                              ...documentationData,
                              managementExplanations: e.target.value,
                            })
                          }
                          disabled={isArchivalMode}
                          className="min-h-32 w-full border-white/10 bg-black/40 text-white placeholder:text-white/50"
                          placeholder="Document management explanations for significant variances..."
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
